import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { Shift } from '../types/user';
import apiService from '../services/api';

// Async thunks
export const fetchShifts = createAsyncThunk(
  'shifts/fetchShifts',
  async (params?: any) => {
    const response = await apiService.getShifts(params);
    return response;
  }
);

export const fetchMyShifts = createAsyncThunk(
  'shifts/fetchMyShifts',
  async () => {
    const response = await apiService.getMyShifts();
    return response;
  }
);

export const fetchCurrentShifts = createAsyncThunk(
  'shifts/fetchCurrentShifts',
  async () => {
    const response = await apiService.getCurrentShifts();
    return response;
  }
);

export const fetchShift = createAsyncThunk(
  'shifts/fetchShift',
  async (shiftId: number) => {
    const response = await apiService.getShift(shiftId);
    return response;
  }
);

export const createShift = createAsyncThunk(
  'shifts/createShift',
  async (shiftData: Partial<Shift>) => {
    const response = await apiService.createShift(shiftData);
    return response;
  }
);

export const updateShift = createAsyncThunk(
  'shifts/updateShift',
  async ({ shiftId, data }: { shiftId: number; data: Partial<Shift> }) => {
    const response = await apiService.updateShift(shiftId, data);
    return response;
  }
);

export const deleteShift = createAsyncThunk(
  'shifts/deleteShift',
  async (shiftId: number) => {
    await apiService.deleteShift(shiftId);
    return shiftId;
  }
);

export const startShift = createAsyncThunk(
  'shifts/startShift',
  async (shiftId: number) => {
    await apiService.startShift(shiftId);
    return shiftId;
  }
);

export const endShift = createAsyncThunk(
  'shifts/endShift',
  async (shiftId: number) => {
    await apiService.endShift(shiftId);
    return shiftId;
  }
);

// Shift filters interface
interface ShiftFilters {
  unit?: number;
  staff_member?: number;
  shift_type?: string;
  status?: string;
  date_range?: {
    start: string;
    end: string;
  };
}

// Shifts state interface
interface ShiftsState {
  shifts: Shift[];
  myShifts: Shift[];
  currentShifts: Shift[];
  selectedShift: Shift | null;
  filters: ShiftFilters;
  loading: boolean;
  myShiftsLoading: boolean;
  currentShiftsLoading: boolean;
  error: string | null;
}

const initialState: ShiftsState = {
  shifts: [],
  myShifts: [],
  currentShifts: [],
  selectedShift: null,
  filters: {},
  loading: false,
  myShiftsLoading: false,
  currentShiftsLoading: false,
  error: null,
};

const shiftsSlice = createSlice({
  name: 'shifts',
  initialState,
  reducers: {
    setSelectedShift: (state, action: PayloadAction<Shift | null>) => {
      state.selectedShift = action.payload;
    },
    setShiftFilters: (state, action: PayloadAction<ShiftFilters>) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearShiftFilters: (state) => {
      state.filters = {};
    },
    clearShiftsError: (state) => {
      state.error = null;
    },
    clearShiftsState: (state) => {
      state.shifts = [];
      state.myShifts = [];
      state.currentShifts = [];
      state.selectedShift = null;
      state.filters = {};
      state.error = null;
    },
    updateShiftStatus: (state, action: PayloadAction<{ shiftId: number; status: string }>) => {
      const { shiftId, status } = action.payload;
      
      // Update in all arrays
      [state.shifts, state.myShifts, state.currentShifts].forEach(shiftArray => {
        const shift = shiftArray.find(s => s.id === shiftId);
        if (shift) {
          shift.status = status as any;
          if (status === 'in_progress') {
            shift.checked_in_at = new Date().toISOString();
          } else if (status === 'completed') {
            shift.checked_out_at = new Date().toISOString();
          }
        }
      });
      
      // Update selected shift
      if (state.selectedShift?.id === shiftId) {
        state.selectedShift.status = status as any;
        if (status === 'in_progress') {
          state.selectedShift.checked_in_at = new Date().toISOString();
        } else if (status === 'completed') {
          state.selectedShift.checked_out_at = new Date().toISOString();
        }
      }
    },
  },
  extraReducers: (builder) => {
    // Fetch shifts
    builder
      .addCase(fetchShifts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchShifts.fulfilled, (state, action) => {
        state.loading = false;
        state.shifts = action.payload;
      })
      .addCase(fetchShifts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch shifts';
      });

    // Fetch my shifts
    builder
      .addCase(fetchMyShifts.pending, (state) => {
        state.myShiftsLoading = true;
        state.error = null;
      })
      .addCase(fetchMyShifts.fulfilled, (state, action) => {
        state.myShiftsLoading = false;
        state.myShifts = action.payload;
      })
      .addCase(fetchMyShifts.rejected, (state, action) => {
        state.myShiftsLoading = false;
        state.error = action.error.message || 'Failed to fetch my shifts';
      });

    // Fetch current shifts
    builder
      .addCase(fetchCurrentShifts.pending, (state) => {
        state.currentShiftsLoading = true;
        state.error = null;
      })
      .addCase(fetchCurrentShifts.fulfilled, (state, action) => {
        state.currentShiftsLoading = false;
        state.currentShifts = action.payload;
      })
      .addCase(fetchCurrentShifts.rejected, (state, action) => {
        state.currentShiftsLoading = false;
        state.error = action.error.message || 'Failed to fetch current shifts';
      });

    // Fetch single shift
    builder
      .addCase(fetchShift.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchShift.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedShift = action.payload;
        // Update shifts list if shift exists
        const index = state.shifts.findIndex(shift => shift.id === action.payload.id);
        if (index !== -1) {
          state.shifts[index] = action.payload;
        }
      })
      .addCase(fetchShift.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch shift';
      });

    // Create shift
    builder
      .addCase(createShift.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createShift.fulfilled, (state, action) => {
        state.loading = false;
        state.shifts.push(action.payload);
        // If it's user's shift, add to myShifts
        // Note: This assumes we have access to current user ID
        // In a real app, you'd need to check if the shift belongs to current user
      })
      .addCase(createShift.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to create shift';
      });

    // Update shift
    builder
      .addCase(updateShift.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateShift.fulfilled, (state, action) => {
        state.loading = false;
        const updateShiftInArray = (shiftArray: Shift[]) => {
          const index = shiftArray.findIndex(shift => shift.id === action.payload.id);
          if (index !== -1) {
            shiftArray[index] = action.payload;
          }
        };
        
        updateShiftInArray(state.shifts);
        updateShiftInArray(state.myShifts);
        updateShiftInArray(state.currentShifts);
        
        if (state.selectedShift?.id === action.payload.id) {
          state.selectedShift = action.payload;
        }
      })
      .addCase(updateShift.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to update shift';
      });

    // Delete shift
    builder
      .addCase(deleteShift.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteShift.fulfilled, (state, action) => {
        state.loading = false;
        state.shifts = state.shifts.filter(shift => shift.id !== action.payload);
        state.myShifts = state.myShifts.filter(shift => shift.id !== action.payload);
        state.currentShifts = state.currentShifts.filter(shift => shift.id !== action.payload);
        
        if (state.selectedShift?.id === action.payload) {
          state.selectedShift = null;
        }
      })
      .addCase(deleteShift.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to delete shift';
      });

    // Start shift
    builder
      .addCase(startShift.fulfilled, (state, action) => {
        shiftsSlice.caseReducers.updateShiftStatus(state, {
          payload: { shiftId: action.payload, status: 'in_progress' },
          type: 'shifts/updateShiftStatus'
        });
      })
      .addCase(startShift.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to start shift';
      });

    // End shift
    builder
      .addCase(endShift.fulfilled, (state, action) => {
        shiftsSlice.caseReducers.updateShiftStatus(state, {
          payload: { shiftId: action.payload, status: 'completed' },
          type: 'shifts/updateShiftStatus'
        });
      })
      .addCase(endShift.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to end shift';
      });
  },
});

// Selectors
export const selectShifts = (state: any) => state.shifts.shifts;
export const selectMyShifts = (state: any) => state.shifts.myShifts;
export const selectCurrentShifts = (state: any) => state.shifts.currentShifts;
export const selectSelectedShift = (state: any) => state.shifts.selectedShift;
export const selectShiftFilters = (state: any) => state.shifts.filters;
export const selectShiftsLoading = (state: any) => state.shifts.loading;
export const selectMyShiftsLoading = (state: any) => state.shifts.myShiftsLoading;
export const selectCurrentShiftsLoading = (state: any) => state.shifts.currentShiftsLoading;
export const selectShiftsError = (state: any) => state.shifts.error;

// Filtered selectors
export const selectFilteredShifts = (state: any) => {
  const shifts = state.shifts.shifts;
  const filters = state.shifts.filters;
  
  return shifts.filter((shift: Shift) => {
    if (filters.unit && shift.unit !== filters.unit) return false;
    if (filters.staff_member && shift.staff_member !== filters.staff_member) return false;
    if (filters.shift_type && shift.shift_type !== filters.shift_type) return false;
    if (filters.status && shift.status !== filters.status) return false;
    
    if (filters.date_range) {
      const shiftDate = new Date(shift.start_datetime);
      const startDate = new Date(filters.date_range.start);
      const endDate = new Date(filters.date_range.end);
      if (shiftDate < startDate || shiftDate > endDate) return false;
    }
    
    return true;
  });
};

// Get upcoming shifts (next 7 days)
export const selectUpcomingShifts = (state: any) => {
  const shifts = state.shifts.myShifts;
  const now = new Date();
  const nextWeek = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
  
  return shifts.filter((shift: Shift) => {
    const shiftStart = new Date(shift.start_datetime);
    return shiftStart >= now && shiftStart <= nextWeek && shift.status === 'scheduled';
  }).sort((a: Shift, b: Shift) => 
    new Date(a.start_datetime).getTime() - new Date(b.start_datetime).getTime()
  );
};

// Get current user's active shift
export const selectCurrentUserShift = (state: any) => {
  return state.shifts.myShifts.find((shift: Shift) => shift.is_current) || null;
};

export const { 
  setSelectedShift, 
  setShiftFilters, 
  clearShiftFilters, 
  clearShiftsError, 
  clearShiftsState,
  updateShiftStatus
} = shiftsSlice.actions;

export default shiftsSlice.reducer;